import React, { FC, useMemo, useState } from 'react';

import { RootLayout } from './layouts/root';
import { GridContainer } from './components/grid-container';
import { TemplateSelector } from './components/template-selector';
import { TemplateRenderer } from './components/template-renderer';

import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { FormattedMessage, useIntl } from 'react-intl';
import { CookiesProvider } from 'react-cookie';
import { BrowserRouter as Router } from 'react-router-dom';

import { FieldData, SignatureField } from './hooks/customer';
import { LanguageProvider } from './providers/translations-provider';
import { ConsentProvider } from './providers/consent-provider';
import { AnalyticsProvider } from './providers/analytics-provider';

import { CustomerConfig, CustomerTemplate } from './types';


export interface AppProps {
    config: CustomerConfig;
}

export const AppRoot: FC<AppProps> = ( props ) => {
    const theme = useMemo( () => {
        // https://in-your-saas.github.io/material-ui-theme-editor/
        return createMuiTheme( props.config.theme )
    }, [ props.config.theme ] );

    return (
        <Router>
            <CookiesProvider>
                <ThemeProvider theme={ theme }>
                    <LanguageProvider>
                        <ConsentProvider>
                            <AnalyticsProvider tracking={props.config.tracking}>
                                <App { ...props }/>
                            </AnalyticsProvider>
                        </ConsentProvider>
                    </LanguageProvider>
                </ThemeProvider>
            </CookiesProvider>
        </Router>
    )
}

const App: FC<AppProps> = ( { config } ) => {
    const { logo, favicon, name, title, templates, fonts, info } = config;

    const [ templateLanguage, setTemplateLanguage ] = useState<string>('de');
    const [ template, setTemplate ] = useState<CustomerTemplate | null>( null );
    const [ data, setData ] = useState<FieldData>();
    const [ signatures, setSignatures ] = useState<SignatureField[]>( [] );

    const intl = useIntl();

    const handleSelectTemplate = ( newTemplate: CustomerTemplate | null ) => {
        setTemplate( newTemplate );
    }

    const handleFieldDataChange = ( data: FieldData ) => {
        setData( data );
    }

    const handleSignatureChange = ( signatures: SignatureField[] ) => {
        setSignatures( signatures );
    }

    const handleTemplateLanguageChange = (language: string) => {
        setTemplateLanguage(language);
    };

    return (
        <RootLayout logo={ logo } favicon={ favicon } name={ name } title={ title } fonts={ fonts } info={ info }>
            <Grid container spacing={ 3 }>
                <GridContainer size={ 5 } title={ intl.formatMessage( { id: 'inputs.title' } ) }>
                    <TemplateSelector
                        templates={ templates }
                        onTemplateChange={ handleSelectTemplate }
                        onFieldDataChange={ handleFieldDataChange }
                        onSignaturesChange={ handleSignatureChange }
                        onTemplateLanguageChange={ handleTemplateLanguageChange }
                    />
                </GridContainer>
                <GridContainer size={ 7 } title={ intl.formatMessage( { id: 'preview.title' } ) }>
                    {
                        template && data
                            ? <TemplateRenderer
                                renderTemplate={ config.renderTemplate }
                                template={ template }
                                data={ data }
                                signatures={ signatures }
                                templateLanguage={ templateLanguage }
                            />
                            : <div>
                                <Typography variant={ 'h6' }>
                                    <FormattedMessage id={ 'preview.missing-data' }/>
                                </Typography>
                            </div>
                    }
                </GridContainer>
            </Grid>
        </RootLayout>
    );
}
